import { useState, useMemo } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { useDebounce } from "@/lib/hooks/use-debounce";
import { cn } from "@/lib/utils";

export interface SearchableComboboxProps<T> {
  value: number | string | null;
  onChange: (value: number | string | null) => void;
  options: T[];
  getOptionValue: (option: T) => number | string;
  getOptionLabel: (option: T) => string;
  /** Server-side filtered list, when provided. */
  searchValue?: string;
  onSearchChange?: (search: string) => void;
  /** Enables internal client-side filter when no `onSearchChange` is given. */
  clientFilter?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  /** Label to show in the trigger when `value` is set but no matching option is loaded. */
  fallbackLabel?: string;
  loading?: boolean;
  triggerClassName?: string;
  disabled?: boolean;
}

export function SearchableCombobox<T>({
  value,
  onChange,
  options,
  getOptionValue,
  getOptionLabel,
  searchValue,
  onSearchChange,
  clientFilter = false,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyMessage = "No results",
  fallbackLabel,
  loading = false,
  triggerClassName,
  disabled = false,
}: SearchableComboboxProps<T>) {
  const [open, setOpen] = useState(false);
  const [internalSearch, setInternalSearch] = useState("");
  const isControlled = onSearchChange !== undefined;
  const search = isControlled ? (searchValue ?? "") : internalSearch;
  const setSearch = isControlled ? onSearchChange! : setInternalSearch;

  const debouncedClientSearch = useDebounce(internalSearch, 150);
  const visibleOptions = useMemo(() => {
    if (isControlled || !clientFilter || !debouncedClientSearch) return options;
    const q = debouncedClientSearch.toLowerCase();
    return options.filter((o) => getOptionLabel(o).toLowerCase().includes(q));
  }, [isControlled, clientFilter, debouncedClientSearch, options, getOptionLabel]);

  const selected = value === null || value === undefined
    ? undefined
    : options.find((o) => getOptionValue(o) === value);
  const triggerLabel = selected
    ? getOptionLabel(selected)
    : value !== null && value !== undefined && fallbackLabel
      ? fallbackLabel
      : "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        type="button"
        disabled={disabled}
        className={cn(
          "flex h-7 w-full items-center justify-between rounded-md border border-input bg-input/20 px-2 text-xs/relaxed cursor-pointer transition-colors hover:bg-input/30 focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-50",
          triggerClassName,
        )}
      >
        <span className={triggerLabel ? "text-foreground truncate" : "text-muted-foreground"}>
          {triggerLabel || placeholder}
        </span>
        <svg width="12" height="12" viewBox="0 0 12 12" className="opacity-50 shrink-0 ml-2">
          <path d="M3 5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        <Input
          autoFocus
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 mb-2 text-sm"
        />
        <div className="max-h-56 overflow-y-auto space-y-0.5">
          {loading && (
            <p className="text-xs text-muted-foreground text-center py-2">Loading…</p>
          )}
          {!loading && visibleOptions.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-2">{emptyMessage}</p>
          )}
          {!loading && visibleOptions.map((option) => {
            const optValue = getOptionValue(option);
            const optLabel = getOptionLabel(option);
            const isSelected = optValue === value;
            return (
              <button
                key={optValue}
                type="button"
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-sm cursor-pointer hover:bg-muted/50 text-left",
                  isSelected && "bg-primary/10 text-primary",
                )}
                onClick={() => {
                  onChange(optValue);
                  setOpen(false);
                  if (!isControlled) setInternalSearch("");
                }}
              >
                {optLabel}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
